import axios from '../axios';
import {
  GET_RESTAURANT_LEADS_BY_ZIPCODE,
  UPDATE_RESTAURANT_LEAD_BY_MARKETING,
  UPDATE_RESTAURANT_LEAD_SHOULD_CONTACT_BY_ZIPCODE,
} from '../ApiUrl';

function normalizePhone(item) {
  const phone = String(item?.phone ?? item?.phone_no ?? item?.mobile ?? '').trim();
  return phone || '—';
}

function mapCurrentStatusLabel(rawStatus) {
  const value = String(rawStatus ?? '')
    .trim()
    .toLowerCase();

  switch (value) {
    case 'not_onboarded_yet':
      return 'Not onboarded yet';
    case 'onboarding_in_progress':
      return 'Onboarding in progress';
    case 'onboarded':
      return 'Onboarded';
    case 'onboarding_failed':
      return 'Onboarding failed';
    case 'contacted_restaurant':
      return 'Contacted restaurant';
    case 'meeting_scheduled':
      return 'Meeting scheduled';
    default:
      return value
        ? value
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase())
        : '—';
  }
}

export function mapMarketingLeadApiToRow(item, index) {
  return {
    id: String(item?._id ?? `lead-${index}`),
    placeId: String(item?.place_id ?? ''),
    name: String(item?.restaurant_name ?? item?.restaurantName ?? item?.name ?? '—'),
    address: String(item?.address ?? item?.formatted_address ?? '—'),
    phone: normalizePhone(item),
    rating: Number.isFinite(Number(item?.rating)) ? Number(item.rating) : null,
    smsSent: Boolean(item?.sms_sent),
    currentStatus: mapCurrentStatusLabel(item?.current_status),
    marketingNotes: String(item?.marketing_notes ?? ''),
    shouldContact: Boolean(item?.should_contact),
    updatedAt: item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—',
    logs: Array.isArray(item?.logs) ? item.logs : [],
    types: Array.isArray(item?.types) ? item.types : [],
    zip: String(item?.zip ?? item?.zipcode ?? item?.pincode ?? '—'),
    meeting_scheduled_at: String(item?.meeting_scheduled_at ?? ''),
  };
}

/**
 * GET /GetRestaurantLeadByZipcode
 * Query: zip|zipcode|pincode, page, limit
 */
export const getRestaurantLeadByZipcodeAPI = async ({
  q,
  zip,
  zipcode,
  pincode,
  page = 1,
  limit = 20,
  sms_sent,
  current_status,
  should_contact,
} = {}) => {
  try {
    const safePage = Math.max(1, parseInt(String(page), 10) || 1);
    let safeLimit = parseInt(String(limit), 10) || 20;
    if (safeLimit < 1) safeLimit = 1;
    if (safeLimit > 200) safeLimit = 200;

    const params = { page: safePage, limit: safeLimit };

    const query = String(q ?? '').trim();
    if (query) params.q = query;
    const zipQuery = String(zip ?? zipcode ?? pincode ?? '').trim();
    if (zipQuery) params.zip = zipQuery;
    if (typeof sms_sent === 'boolean') params.sms_sent = sms_sent;
    if (typeof current_status === 'string' && current_status.trim() !== '') {
      params.current_status = current_status.trim();
    }
    if (typeof should_contact === 'boolean') params.should_contact = should_contact;

    const response = await axios.get(GET_RESTAURANT_LEADS_BY_ZIPCODE, { params });
    return response;
  } catch (error) {
    console.log('getRestaurantLeadByZipcodeAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to load marketing leads.' };
  }
};

export const updateRestaurantLeadByMarketingAPI = async ({
  _id,
  place_id,
  current_status,
  marketing_notes,
  sms_sent,
  call_status,
  calling_note,
  meeting_scheduled_at,
  changed_by_id,
  changed_by_role,
  changed_by_name,
  reason,
  api_name,
} = {}) => {
  try {
    const body = {};
    const id = String(_id ?? '').trim();
    const placeId = String(place_id ?? '').trim();

    if (id) body._id = id;
    if (placeId) body.place_id = placeId;
    if (!body._id && !body.place_id) {
      return { hasError: true, message: 'Lead identifier is required.' };
    }

    // Standard update fields
    if (typeof current_status === 'string' && current_status.trim() !== '') {
      body.current_status = current_status.trim();
    }
    if (typeof marketing_notes === 'string') {
      body.marketing_notes = marketing_notes;
    }
    if (typeof sms_sent === 'boolean') {
      body.sms_sent = sms_sent;
    }

    // Call log fields
    if (typeof call_status === 'string' && call_status.trim() !== '') {
      body.call_status = call_status.trim().toLowerCase();
    }
    if (typeof calling_note === 'string' && calling_note.trim() !== '') {
      body.calling_note = calling_note.trim();
    }
    if (meeting_scheduled_at != null && String(meeting_scheduled_at).trim() !== '') {
      body.meeting_scheduled_at = String(meeting_scheduled_at).trim();
    }

    // Audit meta
    if (changed_by_id != null && String(changed_by_id).trim() !== '') {
      body.changed_by_id = String(changed_by_id).trim();
    }
    if (changed_by_role != null && String(changed_by_role).trim() !== '') {
      body.changed_by_role = String(changed_by_role).trim();
    }
    if (changed_by_name != null && String(changed_by_name).trim() !== '') {
      body.changed_by_name = String(changed_by_name).trim();
    }
    if (reason != null && String(reason).trim() !== '') {
      body.reason = String(reason).trim();
    }
    if (api_name != null && String(api_name).trim() !== '') {
      body.api_name = String(api_name).trim();
    }

    const hasUpdateField =
      Object.prototype.hasOwnProperty.call(body, 'current_status') ||
      Object.prototype.hasOwnProperty.call(body, 'marketing_notes') ||
      Object.prototype.hasOwnProperty.call(body, 'sms_sent') ||
      Object.prototype.hasOwnProperty.call(body, 'call_status');
    if (!hasUpdateField) {
      return { hasError: true, message: 'No valid fields to update.' };
    }

    const response = await axios.put(UPDATE_RESTAURANT_LEAD_BY_MARKETING, body);
    return response;
  } catch (error) {
    console.log('updateRestaurantLeadByMarketingAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to update lead.' };
  }
};

/**
 * PUT /UpdateRestaurantLeadShouldContactByZipcode
 * Bulk-updates should_contact for every lead that matches the given ZIP code.
 *
 * @param {{ zip: string, should_contact: boolean }} params
 */
export const bulkUpdateShouldContactByZipcodeAPI = async ({ zip, should_contact } = {}) => {
  try {
    const normalizedZip = String(zip ?? '').trim();
    if (!normalizedZip) {
      return { hasError: true, message: 'ZIP code is required.' };
    }
    if (typeof should_contact !== 'boolean') {
      return { hasError: true, message: 'should_contact must be true or false.' };
    }

    const response = await axios.put(UPDATE_RESTAURANT_LEAD_SHOULD_CONTACT_BY_ZIPCODE, {
      zip: normalizedZip,
      should_contact,
    });
    return response;
  } catch (error) {
    console.log('bulkUpdateShouldContactByZipcodeAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to bulk update leads.' };
  }
};
